import React, { useEffect, useState } from 'react'
import {
  Image,
  TextInput,
  Alert,
  Platform,
  RefreshControl,
  useWindowDimensions,
} from 'react-native'
import { ArrowLeft, Sparkles, Tag, Trash2, Facebook, Instagram, Video, Check } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { api } from '../lib/api'
import { getProductSuggestions } from '../lib/openrouter'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '../components/Toast'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import {
  Button,
  ButtonText,
  Card,
  Input,
  ScrollView,
  Spinner,
  Text,
  TopBar,
  XStack,
  YStack,
} from '@fliisi/pollen'

const MAX_IMAGE_WIDTH = 480
const MAX_CONTENT_WIDTH = 768

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'ProductDetail'>
  route: RouteProp<RootStackParamList, 'ProductDetail'>
}

interface Product {
  id: string
  title: string
  title_en: string | null
  title_fi: string | null
  description: string
  description_en: string | null
  description_fi: string | null
  price: number
  status: string
  barcode: string
  category_id: string
  created_at: string
  flea_market_id: string | null
  flea_market: { id: string; name: string } | null
  product_images: { id: string; image_url: string }[]
}

type SocialPlatform = 'facebook' | 'instagram' | 'tiktok'

export default function ProductDetailScreen({ navigation, route }: Props) {
  const { productId } = route.params
  const { language, t } = useLanguage()
  const { width: screenWidth } = useWindowDimensions()
  const toast = useToast()
  const { user } = useAuth()
  const isDesktop = screenWidth >= 768

  const [product, setProduct] = useState<Product | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [suggesting, setSuggesting] = useState(false)
  const [titleEn, setTitleEn] = useState('')
  const [titleFi, setTitleFi] = useState('')
  const [descriptionEn, setDescriptionEn] = useState('')
  const [descriptionFi, setDescriptionFi] = useState('')
  const [price, setPrice] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [posting, setPosting] = useState(false)
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<SocialPlatform>>(new Set())

  const imageWidth = isDesktop ? MAX_IMAGE_WIDTH : screenWidth

  const onRefresh = async () => {
    setRefreshing(true)
    await fetchProduct()
    setRefreshing(false)
  }

  useEffect(() => {
    fetchProduct()
  }, [productId])

  const fetchProduct = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`*, product_images (id, image_url), flea_market:flea_markets ( id, name )`)
        .eq('id', productId)
        .single()

      if (error) throw error

      setProduct({
        ...data,
        flea_market: Array.isArray(data.flea_market) ? data.flea_market[0] : data.flea_market,
      } as Product)
      setTitleEn(data.title_en || data.title || '')
      setTitleFi(data.title_fi || data.title || '')
      setDescriptionEn(data.description_en || data.description || '')
      setDescriptionFi(data.description_fi || data.description || '')
      setPrice(data.price?.toString() || '')
    } catch (error) {
      console.error('Error fetching product:', error)
      toast.error('Virhe', 'Tuotetta ei voitu ladata')
      navigation.goBack()
    } finally {
      setLoading(false)
    }
  }

  const saveProduct = async (publish: boolean = false) => {
    if (!price || isNaN(parseFloat(price))) {
      toast.error('Virhe', 'Syota kelvollinen hinta')
      return
    }

    setSaving(true)
    try {
      const barcode = product?.barcode || `P${Date.now().toString(36).toUpperCase()}`

      const { error } = await supabase
        .from('products')
        .update({
          title: titleEn || titleFi || 'Untitled',
          title_en: titleEn || null,
          title_fi: titleFi || null,
          description: descriptionEn || descriptionFi || null,
          description_en: descriptionEn || null,
          description_fi: descriptionFi || null,
          price: parseFloat(price),
          barcode,
          status: publish ? 'for_sale' : 'draft',
        })
        .eq('id', productId)

      if (error) throw error

      toast.success('Valmis', publish ? 'Tuote julkaistu!' : 'Luonnos tallennettu!')
      setTimeout(() => navigation.goBack(), 1000)
    } catch (error: any) {
      console.error('Save error:', error)
      toast.error('Virhe', error.message)
    } finally {
      setSaving(false)
    }
  }

  const deleteProduct = async () => {
    const doDelete = async () => {
      try {
        const { error } = await supabase.from('products').delete().eq('id', productId)
        if (error) throw error
        toast.success('Poistettu', 'Tuote poistettiin')
        setTimeout(() => navigation.goBack(), 800)
      } catch (error: any) {
        toast.error('Virhe', error.message)
      }
    }

    if (Platform.OS === 'web') {
      if (window.confirm('Haluatko varmasti poistaa taman tuotteen?')) {
        await doDelete()
      }
    } else {
      Alert.alert('Poista tuote', 'Haluatko varmasti poistaa taman tuotteen?', [
        { text: 'Peruuta', style: 'cancel' },
        { text: 'Poista', style: 'destructive', onPress: doDelete },
      ])
    }
  }

  const getAISuggestion = async () => {
    const imageUrl = product?.product_images?.[0]?.image_url
    if (!imageUrl) {
      toast.error('Virhe', 'Ei kuvaa AI-analyysille')
      return
    }

    setSuggesting(true)
    try {
      const suggestion = await getProductSuggestions(imageUrl)
      setTitleEn(suggestion.title_en || '')
      setTitleFi(suggestion.title_fi || '')
      setDescriptionEn(suggestion.description_en || '')
      setDescriptionFi(suggestion.description_fi || '')
      if (suggestion.price !== undefined) {
        setPrice(suggestion.price.toFixed(2))
      }
    } catch (error: any) {
      console.error('AI suggestion error:', error)
      toast.error('Virhe', 'AI-ehdotus epäonnistui. Yritä uudelleen.')
    } finally {
      setSuggesting(false)
    }
  }

  const togglePlatform = (platform: SocialPlatform) => {
    setSelectedPlatforms(prev => {
      const next = new Set(prev)
      if (next.has(platform)) {
        next.delete(platform)
      } else {
        next.add(platform)
      }
      return next
    })
  }

  const publishToSocial = async () => {
    if (!product) return
    if (selectedPlatforms.size === 0) {
      toast.error('Virhe', 'Valitse vahintaan yksi kanava')
      return
    }

    const imageUrl = product.product_images?.[0]?.image_url
    if (!imageUrl) {
      toast.error('Virhe', 'Tuotteella ei ole kuvaa julkaisua varten')
      return
    }

    const platformNames: Record<SocialPlatform, string> = {
      facebook: 'Facebook',
      instagram: 'Instagram',
      tiktok: 'TikTok',
    }
    const selectedNames = Array.from(selectedPlatforms).map(p => platformNames[p]).join(', ')

    const doPost = async () => {
      setPosting(true)
      try {
        const title = titleFi || titleEn || product.title || 'Tuote'
        const result = await api.socialPost.fetch({
          title,
          description: descriptionFi || descriptionEn || undefined,
          price: parseFloat(price) || product.price || 0,
          storeName: product.flea_market?.name || undefined,
          imageUrls: [imageUrl],
          platforms: Array.from(selectedPlatforms),
          profileKey: product.flea_market?.id || undefined,
          storeId: product.flea_market?.id || undefined,
          userId: user?.id,
          productIds: [productId],
        })

        toast.success('Julkaistu!', `Tuote julkaistu: ${selectedNames}`)
        setSelectedPlatforms(new Set())
      } catch (error: any) {
        console.error('Social post error:', error)
        const msg = error.message || 'Julkaisu epäonnistui'
        if (msg.includes('not configured') || msg.includes('profile')) {
          toast.error(
            'Tilit puuttuvat',
            'Yhdista sometilit ensin kohdasta Lisaa > Sometilit'
          )
        } else {
          toast.error('Virhe', msg)
        }
      } finally {
        setPosting(false)
      }
    }

    if (Platform.OS === 'web') {
      if (window.confirm(`Julkaistaan tuote seuraaviin kanaviin: ${selectedNames}`)) {
        await doPost()
      }
    } else {
      Alert.alert(
        'Julkaise someen',
        `Julkaistaan tuote seuraaviin kanaviin: ${selectedNames}`,
        [
          { text: 'Peruuta', style: 'cancel' },
          { text: 'Julkaise', onPress: doPost },
        ]
      )
    }
  }

  if (loading) {
    return (
      <YStack f={1} backgroundColor="$background">
        <YStack f={1} jc="center" ai="center"><Spinner size="small" /></YStack>
      </YStack>
    )
  }

  if (!product) {
    return (
      <YStack f={1} backgroundColor="$background">
        <YStack f={1} jc="center" ai="center">
          <Text fontSize={16} color="$colorSecondary">Tuotetta ei löytynyt</Text>
        </YStack>
      </YStack>
    )
  }

  const mainImage = product.product_images?.[0]
  const statusLabel = product.status === 'for_sale' ? 'Myynnissä' : product.status === 'draft' ? 'Luonnos' : product.status

  return (
    <YStack f={1} backgroundColor="$background">
      <ScrollView
        f={1}
        contentContainerStyle={{ maxWidth: MAX_CONTENT_WIDTH, alignSelf: 'center', width: '100%' }}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        keyboardShouldPersistTaps="handled"
      >
        {/* Header */}
        <XStack alignItems="center" justifyContent="space-between" paddingHorizontal="$md" paddingTop={Platform.OS === 'web' ? 16 : 52} paddingBottom="$sm">
          <YStack
            width={40} height={40} borderRadius="$lg" backgroundColor="$backgroundCard"
            jc="center" ai="center" elevation={2}
            onPress={() => navigation.goBack()}
            pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
          >
            <ArrowLeft size={20} color="var(--color)" />
          </YStack>
          <Text fontSize={18} fontFamily="$cursive" color="$color">Muokkaa tuotetta</Text>
          <YStack width={40} />
        </XStack>

        {/* Image */}
        <YStack backgroundColor="$backgroundSecondary" {...(isDesktop && { ai: 'center', paddingVertical: '$md' })}>
          <YStack
            position="relative"
            backgroundColor="$backgroundSecondary"
            width={imageWidth}
            height={imageWidth}
            {...(isDesktop && { borderRadius: '$xl', overflow: 'hidden' })}
          >
            <Image
              source={{ uri: mainImage?.image_url || 'https://via.placeholder.com/400/E0E2DB/858B80?text=No+Image' }}
              style={{ width: imageWidth, height: imageWidth }}
              resizeMode="cover"
            />
            {mainImage?.image_url && (
              <XStack
                position="absolute" bottom="$sm" right="$sm"
                alignItems="center" gap={6}
                backgroundColor="$colorPrimary"
                paddingHorizontal="$md" paddingVertical={10} borderRadius="$full"
                elevation={4}
                opacity={suggesting ? 0.6 : 1}
                onPress={getAISuggestion}
                pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
                disabled={suggesting}
              >
                {suggesting ? (
                  <Spinner size="small" />
                ) : (
                  <>
                    <Sparkles size={16} color="#fff" />
                    <Text color="#fff" fontSize={14} fontWeight="600">AI-ehdotus</Text>
                  </>
                )}
              </XStack>
            )}
          </YStack>
        </YStack>

        {/* Status + Barcode */}
        <XStack alignItems="center" gap={10} paddingHorizontal="$lg" paddingVertical={14}>
          <XStack
            paddingHorizontal="$sm" paddingVertical={6} borderRadius="$full"
            backgroundColor={product.status === 'for_sale' ? '$colorPrimaryMuted' : '$backgroundSecondary'}
          >
            <Text fontSize={13} fontWeight="600" color={product.status === 'for_sale' ? '$colorPrimary' : '#C07A5F'}>
              {statusLabel}
            </Text>
          </XStack>
          {product.barcode && (
            <XStack alignItems="center" gap={6} backgroundColor="$backgroundCard" paddingHorizontal={10} paddingVertical={6} borderRadius="$md">
              <Tag size={14} color="var(--colorSecondary)" />
              <Text fontSize={12} fontFamily="monospace" color="$colorSecondary">{product.barcode}</Text>
            </XStack>
          )}
        </XStack>

        {/* Social media posting */}
        <YStack paddingHorizontal="$lg" paddingBottom={14} gap={10}>
          <Text fontSize={14} fontWeight="600" color="$colorSecondary">Jaa someen:</Text>
          <XStack gap={8} flexWrap="wrap">
            <XStack
              bg={selectedPlatforms.has('instagram') ? '#E1306C' : '$backgroundCard'}
              borderWidth={1.5}
              borderColor={selectedPlatforms.has('instagram') ? '#E1306C' : '$borderColor'}
              px={14} py={8} br={16}
              ai="center" gap={6}
              onPress={() => togglePlatform('instagram')}
              pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
            >
              <Instagram size={16} color={selectedPlatforms.has('instagram') ? '#fff' : '#E1306C'} />
              <Text color={selectedPlatforms.has('instagram') ? '#fff' : '$color'} fontSize={13} fontWeight="600">Instagram</Text>
              {selectedPlatforms.has('instagram') && <Check size={14} color="#fff" />}
            </XStack>
            <XStack
              bg={selectedPlatforms.has('facebook') ? '#1877F2' : '$backgroundCard'}
              borderWidth={1.5}
              borderColor={selectedPlatforms.has('facebook') ? '#1877F2' : '$borderColor'}
              px={14} py={8} br={16}
              ai="center" gap={6}
              onPress={() => togglePlatform('facebook')}
              pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
            >
              <Facebook size={16} color={selectedPlatforms.has('facebook') ? '#fff' : '#1877F2'} />
              <Text color={selectedPlatforms.has('facebook') ? '#fff' : '$color'} fontSize={13} fontWeight="600">Facebook</Text>
              {selectedPlatforms.has('facebook') && <Check size={14} color="#fff" />}
            </XStack>
            <XStack
              bg={selectedPlatforms.has('tiktok') ? '#000' : '$backgroundCard'}
              borderWidth={1.5}
              borderColor={selectedPlatforms.has('tiktok') ? '#000' : '$borderColor'}
              px={14} py={8} br={16}
              ai="center" gap={6}
              onPress={() => togglePlatform('tiktok')}
              pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
            >
              <Video size={16} color={selectedPlatforms.has('tiktok') ? '#fff' : '#000'} />
              <Text color={selectedPlatforms.has('tiktok') ? '#fff' : '$color'} fontSize={13} fontWeight="600">TikTok</Text>
              {selectedPlatforms.has('tiktok') && <Check size={14} color="#fff" />}
            </XStack>
          </XStack>
          {selectedPlatforms.size > 0 && (
            <XStack
              bg="$colorPrimary" px={16} py={10} br={12}
              jc="center" ai="center"
              opacity={posting ? 0.6 : 1}
              onPress={publishToSocial}
              pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
              disabled={posting}
            >
              {posting ? (
                <Spinner size="small" color="#fff" />
              ) : (
                <Text color="#fff" fontSize={14} fontWeight="700">
                  Julkaise ({selectedPlatforms.size})
                </Text>
              )}
            </XStack>
          )}
        </YStack>

        {/* Form */}
        <YStack paddingHorizontal="$lg">
          <YStack marginBottom={18}>
            <Text fontSize={13} fontWeight="600" color="$colorSecondary" marginBottom="$sm" textTransform="uppercase" letterSpacing={0.5}>Otsikko (suomeksi)</Text>
            <TextInput
              style={{ backgroundColor: 'var(--backgroundCard)', borderRadius: 14, padding: 14, fontSize: 16, borderWidth: 1.5, borderColor: 'var(--borderColor)', color: 'var(--color)' }}
              value={titleFi}
              onChangeText={setTitleFi}
              placeholder="Tuotteen nimi suomeksi"
              placeholderTextColor="var(--colorSecondary)"
            />
          </YStack>

          <YStack marginBottom={18}>
            <Text fontSize={13} fontWeight="600" color="$colorSecondary" marginBottom="$sm" textTransform="uppercase" letterSpacing={0.5}>Title (English)</Text>
            <TextInput
              style={{ backgroundColor: 'var(--backgroundCard)', borderRadius: 14, padding: 14, fontSize: 16, borderWidth: 1.5, borderColor: 'var(--borderColor)', color: 'var(--color)' }}
              value={titleEn}
              onChangeText={setTitleEn}
              placeholder="Product name in English"
              placeholderTextColor="var(--colorSecondary)"
            />
          </YStack>

          <YStack marginBottom={18}>
            <Text fontSize={13} fontWeight="600" color="$colorSecondary" marginBottom="$sm" textTransform="uppercase" letterSpacing={0.5}>Kuvaus (suomeksi)</Text>
            <TextInput
              style={{ backgroundColor: 'var(--backgroundCard)', borderRadius: 14, padding: 14, fontSize: 16, borderWidth: 1.5, borderColor: 'var(--borderColor)', color: 'var(--color)', height: 100, textAlignVertical: 'top' }}
              value={descriptionFi}
              onChangeText={setDescriptionFi}
              placeholder="Tuotteen kuvaus"
              placeholderTextColor="var(--colorSecondary)"
              multiline
              numberOfLines={3}
            />
          </YStack>

          <YStack marginBottom={18}>
            <Text fontSize={13} fontWeight="600" color="$colorSecondary" marginBottom="$sm" textTransform="uppercase" letterSpacing={0.5}>Description (English)</Text>
            <TextInput
              style={{ backgroundColor: 'var(--backgroundCard)', borderRadius: 14, padding: 14, fontSize: 16, borderWidth: 1.5, borderColor: 'var(--borderColor)', color: 'var(--color)', height: 100, textAlignVertical: 'top' }}
              value={descriptionEn}
              onChangeText={setDescriptionEn}
              placeholder="Product description"
              placeholderTextColor="var(--colorSecondary)"
              multiline
              numberOfLines={3}
            />
          </YStack>

          <YStack marginBottom={18}>
            <Text fontSize={13} fontWeight="600" color="$colorSecondary" marginBottom="$sm" textTransform="uppercase" letterSpacing={0.5}>Hinta (EUR) *</Text>
            <TextInput
              style={{ backgroundColor: 'var(--backgroundCard)', borderRadius: 14, padding: 14, fontSize: 16, borderWidth: 1.5, borderColor: 'var(--borderColor)', color: 'var(--color)' }}
              value={price}
              onChangeText={setPrice}
              placeholder="0.00"
              placeholderTextColor="var(--colorSecondary)"
              keyboardType="decimal-pad"
            />
          </YStack>

          {/* Action Buttons */}
          <YStack
            backgroundColor="$colorPrimary" borderRadius={14} padding="$md" alignItems="center" marginTop="$sm"
            elevation={2} opacity={saving ? 0.6 : 1}
            onPress={() => saveProduct(true)}
            pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
            disabled={saving}
          >
            {saving ? <Spinner color="#fff" /> : <Text color="#fff" fontSize={17} fontWeight="700">Julkaise</Text>}
          </YStack>

          <YStack
            backgroundColor="$backgroundCard" borderRadius={14} padding="$md" alignItems="center" marginTop={10}
            borderWidth={1.5} borderColor="$colorPrimary"
            opacity={saving ? 0.6 : 1}
            onPress={() => saveProduct(false)}
            pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
            disabled={saving}
          >
            <Text color="$colorPrimary" fontSize={16} fontWeight="600">Tallenna luonnoksena</Text>
          </YStack>

          <XStack
            jc="center" ai="center" gap={6} paddingVertical="$md" marginTop="$sm"
            onPress={deleteProduct}
            pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
          >
            <Trash2 size={16} color="var(--colorError)" />
            <Text color="$colorError" fontSize={15} fontWeight="500">Poista tuote</Text>
          </XStack>
        </YStack>

        <YStack height={32} />
      </ScrollView>
    </YStack>
  )
}
